import { Checkbox, Grid, List, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { canExpertEditCustomer } from "../../user/accessRightChecks";
import CardsList from "../CardsList";
import WhiteCard from "../WhiteCard";
import {
  fetchCustomerCostCenters,
  selectAllCustomerCostCenters,
} from "../customerCostCentersSlice";
import { selectCustomerOrganizationById } from "../customerOrganizationsSlice";
import { ModalState } from "../types/types";
import { sortListItemsByActiveStatus } from "../utils";
import CustomerCostCentersForm from "./CustomerCostCentersForm";

// needed to enable dragging of nested dialogs
const DRAG_HANDLE_SELECTOR = "drag-handle-costCenters";

const CustomerCostCenters = () => {
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });
  const [showArchived, setShowArchived] = useState(false);

  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const customerOrgId = searchParams.get("org");

  const dispatch = useAppDispatch();

  const { status } = useAppSelector((state) => state.customerCostCenters);

  const selectedOrganization = useAppSelector((state) =>
    selectCustomerOrganizationById(state, customerOrgId as string),
  );

  const organizationCostCenters = useAppSelector((state) =>
    selectAllCustomerCostCenters(state),
  );

  const selectedOrganizationCCs = organizationCostCenters.filter(
    (costCenter) => costCenter.customerOrgId === customerOrgId,
  );

  const costCentersByActiveStatus = showArchived
    ? selectedOrganizationCCs.sort(sortListItemsByActiveStatus)
    : selectedOrganizationCCs
        .filter((costCenter) => costCenter.active === !showArchived)
        .sort((a, b) => a.name.localeCompare(b.name));

  const isLoading = status === "pending";

  useEffect(() => {
    if (selectedOrganization?.customerCompanyId) {
      dispatch(
        fetchCustomerCostCenters({
          customerCompanyId: selectedOrganization.customerCompanyId,
        }),
      );
    }
  }, [dispatch, selectedOrganization?.customerCompanyId]);

  const onOpen = (id?: string) => setModalState({ show: true, id });
  const onClose = () => setModalState({ show: false, id: "" });

  return (
    <Grid container flexDirection="column">
      <OverlaySpinner isLoading={isLoading} />
      <Grid item container alignItems="center" padding="19px">
        <Checkbox
          sx={{ color: "#00000085" }}
          checked={showArchived}
          onChange={() => setShowArchived(!showArchived)}
        />
        <Typography variant="secondary">
          {t("customers:costCenters:showArchivedCostCenters")}
        </Typography>
      </Grid>
      {selectedOrganization && (
        <CardsList
          buttonLabel={t("customers:costCenters:costCenter")}
          onClick={(id?: string) => onOpen(id)}
          accessRightChecks={[canExpertEditCustomer]}
        >
          <List disablePadding>
            {costCentersByActiveStatus.map((costCenter) => (
              <WhiteCard
                key={costCenter.customerCCId}
                onClick={() => onOpen(costCenter.customerCCId)}
                primary={costCenter.name}
                active={costCenter.active}
              />
            ))}
          </List>
        </CardsList>
      )}
      <DialogComponent
        variant="card"
        open={modalState.show}
        onClose={onClose}
        sx={{ minWidth: "963px" }}
        dragHandleSelector={DRAG_HANDLE_SELECTOR}
      >
        <CustomerCostCentersForm
          customerCCId={modalState.id}
          onClose={onClose}
        />
      </DialogComponent>
    </Grid>
  );
};

export default CustomerCostCenters;
